import React, { useState, useEffect, useMemo } from 'react';
import './App.css';
import {
  Container,
  Box,
  CircularProgress,
  Alert,
  ThemeProvider,
  CssBaseline,
} from '@mui/material';
import { Typography } from '@mui/material';

// Import types
import { RoadmapItem, Project } from './types';

// Import theme
import { darkTheme } from './theme';

// Import hooks
import { useLinearData, useScrollBanner } from './hooks';

// Import utils
import {
  sortProjects,
  getDefaultProject,
  filterIssuesByProject,
  groupIssuesByState,
  calculateCompletionPercentage
} from './utils';

// Import components
import {
  Header,
  PlayButton,
  ProgressBar,
  VersionFilter,
  ProjectDetailsPanel,
  RoadmapColumn,
  FooterBanner,
  IssueModal,
  ProjectModal
} from './components';

function App() {
  // State for modals and filters
  const [selectedIssue, setSelectedIssue] = useState<RoadmapItem | null>(null);
  const [modalProject, setModalProject] = useState<Project | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');

  // Custom hooks
  const { issues, projects, loading, error } = useLinearData();
  const showBanner = useScrollBanner();

  const sortedProjects = useMemo(() => sortProjects(projects), [projects]);

  useEffect(() => {
    if (sortedProjects.length === 0) return;
    const selectedStillExists = sortedProjects.some(project => project.id === selectedProjectId);
    if (!selectedProjectId || !selectedStillExists) {
      const defaultProject = getDefaultProject(sortedProjects);
      if (defaultProject) {
        setSelectedProjectId(defaultProject.id);
      }
    }
  }, [sortedProjects, selectedProjectId]);

  const activeProject = sortedProjects.find(project => project.id === selectedProjectId);

  // Derived state
  const allFilteredIssues = activeProject
    ? filterIssuesByProject(issues, activeProject)
    : issues;
  const groupedIssues = groupIssuesByState(allFilteredIssues);
  const completionPercentage = calculateCompletionPercentage(allFilteredIssues);

  // Event handlers
  const handleCardClick = (item: RoadmapItem) => {
    setSelectedIssue(item);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedIssue(null);
  };

  const handleProjectClick = (project: Project) => {
    setSelectedProjectId(project.id);
  };

  const handleOpenProjectModal = (project: Project) => {
    setModalProject(project);
    setProjectModalOpen(true);
  };

  const handleCloseProjectModal = () => {
    setProjectModalOpen(false);
    setModalProject(null);
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box sx={{
        minHeight: '100vh',
        backgroundImage: 'url(/background.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backdropFilter: 'blur(5px)',
          background: 'rgba(0, 0, 0, 0.4)',
          zIndex: 1,
        },
        pt: 3,
        pb: { xs: 8, sm: 10 },
      }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 4, sm: 6 }, position: 'relative', zIndex: 2 }}>
          <Header />

          <PlayButton />

          <ProgressBar
            projectName={activeProject?.name || 'Project'}
            completionPercentage={completionPercentage}
            completedCount={allFilteredIssues.filter(issue => issue.state.name === 'Done').length}
            totalCount={allFilteredIssues.length}
          />

          <VersionFilter
            projects={sortedProjects}
            selectedProjectId={selectedProjectId}
            onProjectClick={handleProjectClick}
          />

          {loading && (
            <Box display="flex" justifyContent="center" sx={{ py: 6 }}>
              <Box textAlign="center">
                <CircularProgress size={40} sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Loading your roadmap...
                </Typography>
              </Box>
            </Box>
          )}

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 4,
                borderRadius: 2,
                background: 'rgba(244, 67, 54, 0.1)',
                border: '1px solid rgba(244, 67, 54, 0.3)'
              }}
            >
              Error loading data: {error}. Make sure your backend server is running and `REACT_APP_API_URL` points to it.
            </Alert>
          )}

          {!loading && !error && (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: '300px 1fr',
                md: '320px repeat(4, 1fr)'
              },
              gap: 3
            }}>
              {/* Project Details Panel */}
              <Box sx={{
                gridColumn: { xs: '1', sm: '1', md: '1' },
                gridRow: { xs: '1', sm: '1', md: '1' }
              }}>
                <ProjectDetailsPanel
                  currentProject={activeProject}
                  onOpenProjectModal={handleOpenProjectModal}
                />
              </Box>

              {/* Roadmap Columns */}
              <Box sx={{
                gridColumn: { xs: '1', sm: '2', md: '2' },
                gridRow: { xs: '2', sm: '1', md: '1' }
              }}>
                <RoadmapColumn
                  title="To Do"
                  items={groupedIssues.todo}
                  chipColor="default"
                  onItemClick={handleCardClick}
                />
              </Box>

              <Box sx={{
                gridColumn: { xs: '1', sm: '2', md: '3' },
                gridRow: { xs: '3', sm: '1', md: '1' }
              }}>
                <RoadmapColumn
                  title="In Progress"
                  items={groupedIssues.inProgress}
                  chipColor="primary"
                  onItemClick={handleCardClick}
                />
              </Box>

              <Box sx={{
                gridColumn: { xs: '1', sm: '1', md: '4' },
                gridRow: { xs: '4', sm: '2', md: '1' }
              }}>
                <RoadmapColumn
                  title="In Testing"
                  items={groupedIssues.inReview}
                  chipColor="warning"
                  onItemClick={handleCardClick}
                />
              </Box>

              <Box sx={{
                gridColumn: { xs: '1', sm: '2', md: '5' },
                gridRow: { xs: '5', sm: '2', md: '1' }
              }}>
                <RoadmapColumn
                  title="Done"
                  items={groupedIssues.done}
                  chipColor="success"
                  onItemClick={handleCardClick}
                />
              </Box>
            </Box>
          )}

          {/* Modals */}
          <IssueModal
            open={modalOpen}
            issue={selectedIssue}
            onClose={handleCloseModal}
          />

          <ProjectModal
            open={projectModalOpen}
            project={modalProject}
            onClose={handleCloseProjectModal}
          />
        </Container>
      </Box>

      <FooterBanner showBanner={showBanner} />
    </ThemeProvider>
  );
}

export default App;